import { Component, Input, Output, EventEmitter, OnInit, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_URL } from '../../../../config';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
  }
}


interface AuditLog {
  id?: string;
  message: string;
  createdTime: number;
  type: string;
  severity: 'info' | 'warning' | 'error';
  prompt?: any;
  promptId?: string;
  consultantName?: string;
  presentRole?: string;
  interviewingRole?: string;
  interviewingClient?: string;
  presentClient?: string;
  previousClient?: string;
  interviewType?: string;
  jobDescription?: string;
  userMessageText?: string;
  answerText?: string;
  raw?: any;
}

@Component({
  selector: 'app-user-audit-detail',
  templateUrl: './user-audit.component.html',
  styleUrls: ['./user-audit.component.scss']
})
export class UserAuditComponent implements OnInit {
    copiedIp: string | null = null;
  @Input() user: any;
  @Input() sessionId?: string;
  @Output() back = new EventEmitter<void>();

  isProfileOpen = false; // User Profile Card visibility
isSummaryOpen = false; // Session Summary Card visibility
isAnalyticsOpen = false;

  // Filtering
  showFilter = false;
  searchTerm = '';
  selectedFilters: string[] = [];
  selectedSeverities: string[] = [];
  filterTypes = {
    interview: true,
    prompt: true,
    userMessage: true
  };

  // Expanded states
  expandedLog: string | null = null;
  expandedPrompt: string | null = null;
  expandedRaw: string | null = null;

  // Data
  logChain: AuditLog[] = [];
  filteredLogChain: AuditLog[] = [];
  detectedPatterns: string[] = [];
  
  // Loading states
  loading = true;
  loadingSummary = false;
  aiSummary = '';
  userDetails: any = null;

  // Constants
  private readonly LOG_TYPES = {
    INTERVIEW_CREATED: 'interview',
    PROMPT_UPDATED: 'prompt',
    USER_MESSAGE: 'userMessage'
  };

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.initializeLogs();
    this.analyzeLogs();
    this.fetchUserDetails();

  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showFilter && !(event.target as Element).closest('.filter-group')) {
      this.showFilter = false;
    }
  }

  // Core Methods
  initializeLogs(): void {
    if (!this.user) {
      this.loading = false;
      return;
    }

    // Handle LoginLogDetailsDTO format with logActions and logChatHistory
    if (this.user.logActions || this.user.logChatHistory) {
      this.logChain = [];
      
      // Process logActions (interview created)
      if (Array.isArray(this.user.logActions)) {
        this.user.logActions.forEach((action: any) => {
          this.logChain.push({
            id: action.id,
            message: `Created interview: ${action.name}`,
            createdTime: action.timestamp,
            type: this.LOG_TYPES.INTERVIEW_CREATED,
            severity: 'info',
            consultantName: action.promptMemory?.consultantName,
            presentRole: action.promptMemory?.presentRole,
            interviewingRole: action.promptMemory?.interviewingRole,
            interviewingClient: action.promptMemory?.interviewingClient,
            presentClient: action.promptMemory?.presentClient,
            previousClient: action.promptMemory?.previousClient,
            interviewType: action.promptMemory?.interviewType,
            jobDescription: action.promptMemory?.jobDescription,
            prompt: action.promptMemory,
            raw: action
          });
        });
      }
      
      // Process logChatHistory (user messages)
      if (Array.isArray(this.user.logChatHistory)) {
        this.user.logChatHistory.forEach((chatLog: any) => {
          this.logChain.push({
            id: chatLog.id,
            message: chatLog.name || 'User Query',
            createdTime: chatLog.timestamp,
            type: this.LOG_TYPES.USER_MESSAGE,
            severity: 'info',
            promptId: chatLog.promptId,
            userMessageText: chatLog.chatHistory?.question,
            answerText: chatLog.chatHistory?.answer,
            raw: {
              ...chatLog,
              answer: chatLog.chatHistory?.answer,
              question: chatLog.chatHistory?.question
            }
          });
        });
      }
    } else if (Array.isArray(this.user)) {
      // Direct array of logs from console
      this.logChain = this.user.map(log => this.normalizeLog(log));
    } else if (Array.isArray(this.user.changes)) {
      // Legacy format with changes array
      this.logChain = this.user.changes.map((change: string) => {
        try {
          const parsed = JSON.parse(change);
          return this.normalizeLog(parsed);
        } catch {
          return this.normalizeLog({ raw: change });
        }
      });
    } else if (this.user.logs) {
      // Another possible format
      this.logChain = this.user.logs.map((log: any) => this.normalizeLog(log));
    }

    // Sort by timestamp
    this.logChain.sort((a, b) => a.createdTime - b.createdTime);
    
    // Generate unique IDs if missing
    this.logChain.forEach((log, index) => {
      log.id = log.id || `log-${Date.now()}-${index}`;
    });

    this.filteredLogChain = [...this.logChain];
    this.loading = false;
  }

  normalizeLog(rawLog: any): AuditLog {
    // Extract log from possible nested structures
    const log = rawLog.prompt ? { ...rawLog.prompt, ...rawLog } : rawLog;
    
    // Determine log type
    const type = this.determineLogType(log);
    const severity = this.determineSeverity(log);
    
    // Build normalized log
    return {
      id: log.id || log.promptId,
      message: log.message || 'Log entry',
      createdTime: log.createdTime || Date.now(),
      type,
      severity,
      consultantName: log.consultantName,
      presentRole: log.presentRole,
      interviewingRole: log.interviewingRole,
      interviewingClient: log.interviewingClient,
      presentClient: log.presentClient,
      previousClient: log.previousClient,
      interviewType: log.interviewType,
      jobDescription: log.jobDescription,
      userMessageText: log.userMessageText,
      prompt: log.prompt || (typeof log.prompt === 'string' ? { prompt: log.prompt } : null),
      raw: log
    };
  }

  determineLogType(log: any): string {
    if (log.message?.toLowerCase().includes('created interview')) {
      return this.LOG_TYPES.INTERVIEW_CREATED;
    }
    if (log.userMessageText) {
      return this.LOG_TYPES.USER_MESSAGE;
    }
    if (log.prompt || log.promptId) {
      return this.LOG_TYPES.PROMPT_UPDATED;
    }
    return 'activity';
  }

  determineSeverity(log: any): 'info' | 'warning' | 'error' {
    // Logic to determine severity based on log content
    if (log.message?.toLowerCase().includes('error')) return 'error';
    if (log.userMessageText?.includes('test') || log.userMessageText?.includes('what is')) return 'info';
    return 'info';
  }

  // UI Helper Methods
  getLogAction(log: AuditLog): string {
    if (log.type === this.LOG_TYPES.INTERVIEW_CREATED) return 'Interview Created';
    if (log.type === this.LOG_TYPES.USER_MESSAGE) return 'User Query';
    if (log.type === this.LOG_TYPES.PROMPT_UPDATED) return 'Prompt Updated';
    return 'Activity Recorded';
  }

  getLogType(log: AuditLog): string {
    return log.type.charAt(0).toUpperCase() + log.type.slice(1);
  }

  getLogTypeClass(log: AuditLog): string {
    return log.type.toLowerCase();
  }

  getLogSeverity(log: AuditLog): string {
    return `severity-${log.severity}`;
  }

  getLogIcon(log: AuditLog): string {
    switch (log.type) {
      case this.LOG_TYPES.INTERVIEW_CREATED: return 'assignment';
      case this.LOG_TYPES.USER_MESSAGE: return 'question_answer';
      case this.LOG_TYPES.PROMPT_UPDATED: return 'edit_note';
      default: return 'info';
    }
  }

  getLogSummary(log: AuditLog): string {
    if (log.type === this.LOG_TYPES.INTERVIEW_CREATED) {
      return `Created interview for ${log.interviewingRole || 'unknown role'} at ${log.interviewingClient || 'unknown client'}`;
    }
    if (log.type === this.LOG_TYPES.USER_MESSAGE) {
      return `User asked: "${log.userMessageText?.substring(0, 80)}${log.userMessageText && log.userMessageText.length > 80 ? '...' : ''}"`;
    }
    return log.message || 'No summary available';
  }

  formatTimestamp(ts: number): string {
    if (!ts) return 'Unknown time';
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  getTimeAgo(ts: number): string {
    const diff = Date.now() - ts;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  }

  // Filtering Methods
  applySearch(): void {
    if (!this.searchTerm.trim()) {
      this.filteredLogChain = [...this.logChain];
      return;
    }

    const term = this.searchTerm.toLowerCase();
    this.filteredLogChain = this.logChain.filter(log => {
      return (
        log.message?.toLowerCase().includes(term) ||
        log.userMessageText?.toLowerCase().includes(term) ||
        log.consultantName?.toLowerCase().includes(term) ||
        log.interviewingRole?.toLowerCase().includes(term) ||
        log.presentClient?.toLowerCase().includes(term)
      );
    });
  }

  updateFilters(): void {
    this.selectedFilters = [];
    if (this.filterTypes.interview) this.selectedFilters.push('interview');
    if (this.filterTypes.prompt) this.selectedFilters.push('prompt');
    if (this.filterTypes.userMessage) this.selectedFilters.push('userMessage');
    
    this.applyFilters();
  }

  toggleSeverity(severity: string): void {
    const index = this.selectedSeverities.indexOf(severity);
    if (index > -1) {
      this.selectedSeverities.splice(index, 1);
    } else {
      this.selectedSeverities.push(severity);
    }
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.logChain];
    
    // Apply type filters
    if (this.selectedFilters.length > 0) {
      filtered = filtered.filter(log => this.selectedFilters.includes(log.type));
    }
    
    // Apply severity filters
    if (this.selectedSeverities.length > 0) {
      filtered = filtered.filter(log => this.selectedSeverities.includes(log.severity));
    }
    
    // Apply search
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        JSON.stringify(log).toLowerCase().includes(term)
      );
    }
    
    this.filteredLogChain = filtered;
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedFilters = [];
    this.selectedSeverities = [];
    this.filterTypes = {
      interview: true,
      prompt: true,
      userMessage: true
    };
    this.filteredLogChain = [...this.logChain];
  }

  // Analytics Methods
  analyzeLogs(): void {
    this.detectedPatterns = [];
    
    // Check for rapid succession of logs
    if (this.logChain.length > 2) {
      const timeDiffs = [];
      for (let i = 1; i < this.logChain.length; i++) {
        const diff = this.logChain[i].createdTime - this.logChain[i-1].createdTime;
        timeDiffs.push(diff);
      }
      const avgDiff = timeDiffs.reduce((a, b) => a + b, 0) / timeDiffs.length;
      if (avgDiff < 5000) { // Less than 5 seconds average
        this.detectedPatterns.push('Rapid activity detected');
      }
    }
    
    // Check for role switching
    const roles = this.logChain.map(log => log.interviewingRole).filter(Boolean);
    const uniqueRoles = [...new Set(roles)];
    if (uniqueRoles.length > 1) {
      this.detectedPatterns.push(`Multiple roles: ${uniqueRoles.join(', ')}`);
    }
    
    // Check for test patterns
    const testMessages = this.logChain.filter(log => 
      log.userMessageText?.toLowerCase().includes('test') ||
      log.userMessageText?.toLowerCase().includes('hello')
    ).length;
    
    if (testMessages > 2) {
      this.detectedPatterns.push('Test message pattern detected');
    }
  }

  // Statistics Methods
  calculateSessionDuration(): string {
    if (!this.user?.loginTime) return 'N/A';
    
    const loginTime = this.user.loginTime;
    // If logout time exists, use it; otherwise use current time for active sessions
    const endTime = this.user.logoutTime || Date.now();
    const duration = endTime - loginTime;
    
    const hours = Math.floor(duration / 3600000);
    const minutes = Math.floor((duration % 3600000) / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  calculateAverageTPS(): number {
    if (this.logChain.length < 2) return 0;
    const duration = this.calculateSessionDurationInSeconds();
    if (duration === 0) return 0;
    return Math.round((this.logChain.length / duration) * 10) / 10;
  }

  calculateSessionDurationInSeconds(): number {
    if (this.logChain.length < 2) return 0;
    const first = this.logChain[0].createdTime;
    const last = this.logChain[this.logChain.length - 1].createdTime;
    return (last - first) / 1000;
  }

  countUniquePrompts(): number {
    const prompts = this.logChain.map(log => log.prompt?.id).filter(Boolean);
    return new Set(prompts).size;
  }

  countUniqueRoles(): number {
    const roles = this.logChain.map(log => log.interviewingRole).filter(Boolean);
    return new Set(roles).size;
  }

  calculateAvgPromptLength(): number {
    const prompts = this.logChain
      .map(log => log.prompt?.prompt)
      .filter(Boolean)
      .map(p => p.length);
    
    if (prompts.length === 0) return 0;
    return Math.round(prompts.reduce((a, b) => a + b, 0) / prompts.length);
  }

  getFirstLogTime(): number {
    return this.logChain[0]?.createdTime || Date.now();
  }

  getLastLogTime(): number {
    return this.logChain[this.logChain.length - 1]?.createdTime || Date.now();
  }

  getMostRecentInterviewType(): string {
    const lastInterview = [...this.logChain]
      .reverse()
      .find(log => log.interviewType);
    return lastInterview?.interviewType || 'N/A';
  }

  getMostRecentConsultant(): string {
    const lastConsultant = [...this.logChain]
      .reverse()
      .find(log => log.consultantName);
    return lastConsultant?.consultantName || 'N/A';
  }

  getFilteredTypes(): string {
    const types = this.filteredLogChain.map(log => log.type);
    const uniqueTypes = [...new Set(types)];
    return uniqueTypes.join(', ') || 'All types';
  }

  detectPatterns(): boolean {
    return this.detectedPatterns.length > 0;
  }

  // UI Interaction Methods
  toggleLogExpansion(log: AuditLog): void {
    this.expandedLog = this.expandedLog === (log.id ?? null) ? null : (log.id ?? null);
  }

  togglePromptPreview(logId: string): void {
    this.expandedPrompt = this.expandedPrompt === logId ? null : logId;
  }

  toggleRawData(logId: string): void {
    this.expandedRaw = this.expandedRaw === logId ? null : logId;
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  scrollToBottom(): void {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }

  toggleProfileCard(): void {
  this.isProfileOpen = !this.isProfileOpen;
}

toggleSessionSummary(): void {
  this.isSummaryOpen = !this.isSummaryOpen;
}

toggleAnalyticsCard(): void {
    this.isAnalyticsOpen = !this.isAnalyticsOpen;
  }

  // Download/Export Methods
  downloadLogs(): void {
  const logsToDownload = this.filteredLogChain.length ? this.filteredLogChain : this.logChain;

  // Convert objects → CSV rows
  const headers = [
    'id',
    'createdTime',
    'type',
    'severity',
    'message',
    'userMessageText',
    'consultantName',
    'interviewingRole',
    'interviewingClient',
    'presentClient'
  ];

  const csvRows = [];
  csvRows.push(headers.join(','));

  logsToDownload.forEach(log => {
    const row = [
      log.id ?? '',
      log.createdTime ?? '',
      log.type ?? '',
      log.severity ?? '',
      (log.message || '').replace(/,/g, ' '),
      (log.userMessageText || '').replace(/,/g, ' '),
      log.consultantName ?? '',
      log.interviewingRole ?? '',
      log.interviewingClient ?? '',
      log.presentClient ?? ''
    ];
    csvRows.push(row.join(','));
  });

  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `audit-logs-${Date.now()}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}


  exportToPDF(): void {
  // Create a new jsPDF instance
  const doc = new jsPDF();

  // Title
  doc.setFontSize(16);
  doc.text('Audit Logs', 20, 20);

  // Table headers
  const headers = [
    'ID', 'Created Time', 'Type', 'Severity', 'Message', 'User Message', 
    'Consultant Name', 'Interviewing Role', 'Interviewing Client', 'Present Client'
  ];

  // Data preparation
  const rows = this.filteredLogChain.map(log => [
    log.id ?? '',
    this.formatTimestamp(log.createdTime),
    this.getLogType(log),
    this.getLogSeverity(log),
    log.message || '',
    log.userMessageText || '',
    log.consultantName || '',
    log.interviewingRole || '',
    log.interviewingClient || '',
    log.presentClient || ''
  ]);

  // Set font size for table data
  doc.setFontSize(12);

  // Draw the table with headers and rows (pass both doc and options)
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 30, // Start after the title
    margin: { horizontal: 10 },
  });

  // Save the PDF
  doc.save(`audit-logs-${Date.now()}.pdf`);
}


  // Original methods (keeping for compatibility)
  fetchUserDetails(): void {
    // If user object has userName from LoginLogDetailsDTO, use it directly
    if (this.user?.userName) {
      this.userDetails = {
        ...this.user,
        name: this.user.userName,
        avatarUrl: null,
        email: '-',
        device: this.user.device || '-',
        ipAddress: this.user.ipAddress || '-',
        location: this.user.location || '-',
        sessionDuration: this.calculateSessionDuration(),
        status: 'Active'
      };
      
      // Fetch additional user details if loginUserId is available
      if (this.user.loginUserId) {
        this.http.get<any>(`${API_URL}/users/${this.user.loginUserId}`).subscribe(
          apiUserDetails => {
            this.userDetails = {
              ...this.userDetails,
              name: this.user.userName || `${apiUserDetails.firstName} ${apiUserDetails.lastName}`.trim(),
              email: apiUserDetails.email || '-',
              avatarUrl: apiUserDetails.avatarUrl || null
            };
          },
          error => {
            console.error('Failed to fetch additional user details:', error);
          }
        );
      }
      this.loading = false;
      return;
    }
    
    if (this.user?.loginUserId) {
      this.loading = true;
      this.http.get<any>(`${API_URL}/users/${this.user.loginUserId}`).subscribe(
        userDetails => {
          this.userDetails = {
            ...this.user,
            name: `${userDetails.firstName} ${userDetails.lastName}`.trim(),
            avatarUrl: this.user.avatar || userDetails.avatarUrl || null,
            email: userDetails.email || this.user.email || '-',
            device: this.user.device || userDetails.device || '-',
            ip: this.user.ip || userDetails.ip || '-',
            location: this.user.location || userDetails.location || '-',
            sessionDuration: userDetails.sessionDuration || '-',
            status: userDetails.status || 'Unknown',
          };
          this.loading = false;
        },
        error => {
          console.error('Failed to fetch user details:', error);
          this.loading = false;
        }
      );
    } else {
      // Use data from logs if available
      const firstLog = this.logChain[0];
      if (firstLog) {
        this.userDetails = {
          name: firstLog.consultantName || 'Unknown User',
          email: '-',
          device: 'Web Browser',
          ip: '127.0.0.1',
          location: 'Unknown',
          sessionDuration: this.calculateSessionDuration(),
          status: 'Active'
        };
      }
      this.loading = false;
    }
  }

  async generateSummary(): Promise<void> {
    this.loadingSummary = true;
    
    // Simulate AI analysis
    setTimeout(() => {
      const anomalies = this.detectedPatterns.length > 0 
        ? `Detected ${this.detectedPatterns.length} pattern${this.detectedPatterns.length > 1 ? 's' : ''}.`
        : 'No anomalies detected.';
      
      const activitySummary = this.logChain.length > 10 
        ? 'High activity session with multiple interactions.' 
        : 'Moderate activity session.';
      
      this.aiSummary = `
        Session Analysis Summary:
        
        • ${activitySummary}
        • ${this.logChain.length} total interactions recorded
        • Session duration: ${this.calculateSessionDuration()}
        • Average TPS: ${this.calculateAverageTPS()}
        • ${anomalies}
        
        This appears to be ${this.detectedPatterns.length > 0 ? 'an irregular' : 'a normal'} session.
        ${this.detectedPatterns.length > 0 ? 'Review recommended patterns.' : 'No action required.'}
      `.trim();
      
      this.loadingSummary = false;
    }, 2000);
  }

  copyIpAddress(ipAddress: string): void {
    if (!ipAddress) return;
    this.copyTextToClipboard(ipAddress, () => {
      this.copiedIp = ipAddress;
      setTimeout(() => {
        this.copiedIp = null;
      }, 1500);
      // Optional: You can add a toast notification here
      console.log('IP address copied to clipboard');
    }, (err) => {
      this.copiedIp = null;
      console.error('Failed to copy IP address:', err);
    });
  }

  /**
   * Robust clipboard copy with fallback for iframe and browser limitations
   */
  copyTextToClipboard(text: string, onSuccess?: () => void, onError?: (err: any) => void) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => onSuccess && onSuccess())
        .catch(err => {
          this.fallbackCopyTextToClipboard(text, onSuccess, onError);
        });
    } else {
      this.fallbackCopyTextToClipboard(text, onSuccess, onError);
    }
  }

  fallbackCopyTextToClipboard(text: string, onSuccess?: () => void, onError?: (err: any) => void) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (successful) {
        onSuccess && onSuccess();
      } else {
        onError && onError('execCommand failed');
      }
    } catch (err) {
      onError && onError(err);
    }
  }
  

  goBack(): void {
    this.back.emit();
  }
}